import { Injectable, signal } from '@angular/core';
import { IWHWorkObjectCrudStore } from '../app-stores/w-h-work-object-stores/w-h-work-object-crud-store/w-h-work-object-crud-store.reducer';
import {
  IWHCustomerDTO,
  WHCustomerDOM,
  IWHCustomerPreviewDTO,
  WHCustomerPreviewDOM,
  WHFileDOM,
  WHWorkObjectTypeENUM,
  IWHBomNodeDTO,
  WHNestedNodeDOM,
  IWHEquipmentPreviewDTO,
  WHEquipmentPreviewDOM,
  IWHEquipmentDTO,
  WHEquipmentDOM,
  IWHMaterialPageResultDTO,
  WHMaterialPageResultDOM,
  WHMaterialDOM,
  IWHMaterialDTO,
  IWHMaterialInventoryDTO,
  WHMaterialInventoryDOM,
  IWHToolsPreviewDTO,
  WHToolPreviewDOM,
  IWHToolDTO,
  WHToolDOM,
  IWHFlowMetadataDTO,
  WHFlowMetadataDOM,
  IWHWorkObjectPreviewDTO,
  WHWorkObjectPreviewDOM,
  WHChecklistDefinitionDOM,
  WHWorkObjectTemplatePreviewDOM,
  IWHWorkObjectTemplatePreviewDTO,
  IWHWorkObjectTemplateDTO,
  WHWorkObjectTemplateDOM,
  IWHChecklistDefinitionDTO,
  WHChecklistInstancePreviewDOM,
  IWHChecklistInstancePreviewDTO,
  IWHChecklistInstanceDTO,
  WHChecklistInstanceDOM,
  IWHWorkerDTO,
  IWHLocationDTO,
  WHCheckInstanceFileDOM,
  IWHCheckInstanceFileDTO,
  WHWorkerDOM,
  IWHEntryPreviewDTO,
  WHEntryPreviewDOM,
  WHTravelEntryDOM,
  IWHTravelEntryDTO,
  IWHWorkingTimEntryDTO,
  WHWorkingTimEntryDOM,
  IWHExpenseEntryDTO,
  WHExpenseEntryDOM,
  IWHDefectEntryDTO,
  WHDefectEntryDOM,
  IWHMaterialConsumptionEntryDTO,
  WHMaterialConsumptionEntryDOM,
  IWHMeasurementEntryDTO,
  WHMeasurementEntryDOM,
  IWHEntryDTO,
  WHEntryDOM,
  IWHWorkObjectDTO,
  WHWorkObjectDOM,
  IWHWorkOrderFileDTO,
  IWHCaseWorkObjectPreviewDTO,
  WHCaseWorkObjectPreviewDOM,
  IWHCaseWorkObjectDTO,
  WHProjectWorkObjectDOM,
  IWHProjectWorkObjectDTO,
  IWHProjectWorkObjectPreviewDTO,
  WHProjectWorkObjectPreviewDOM,
  WHCaseWorkObjectDOM,
  IWHEntryFilePreviewDTO,
  WHEntryFilePreviewDOM,
  IWHWorkOrderDTO,
  WHWorkOrderDOM,
  IWHEntryFileDTO,
  IWHStandingOrderWorkObjectDTO,
  WHStandingOrderWorkObjectDOM,
  IWHStandingOrderWorkObjectPreviewDTO,
  WHStandingOrderWorkObjectPreviewDOM,
  WHEntryFileDOM,
  IWHWorkStepDTO,
  WHWorkStepDOM,
  WHReportFilePreviewDOM,
  IWHReportFilePreviewDTO,
} from '@workheld/workheld-shared-lib';

@Injectable()
export class DataModelHelperService {
  materialInventoryType = signal('');

  // REMOVE NULL FIELDS
  public removeObjectNullFields(dataObject: any): any {
    const objectPayload: any = {};
    Object.entries(dataObject).forEach((property: any[]) => {
      if (
        property[1] !== null &&
        property[1] !== undefined &&
        property[1] !== ''
      ) {
        // property[0]: key; property[1]: value
        objectPayload[property[0]] = property[1];
      }
    });
    return objectPayload;
  }

  // LOCATION - PLACE (new Places API)
  public initLocationDtoFromGooglePlace(
    place: google.maps.places.Place,
  ): IWHLocationDTO {
    const location = place.location;
    return {
      name: this.extractPlaceName(place.displayName, place.types),
      formattedAddress: place.formattedAddress ?? null,
      latitude: location ? location.lat() : null,
      longitude: location ? location.lng() : null,
      countryCodeISO: this.initLocationCountryISOFromPlaceAddressComponents(
        place.addressComponents,
      ),
      exact: true,
    } as any;
  }

  // LOCATION - GEOCODER (still used by map click / pin drag / locate-by-coords)
  public initLocationDtoFromGoogleGeocoderResultArray(
    geocoderResults: google.maps.GeocoderResult[],
  ): IWHLocationDTO {
    const geocoderResult = geocoderResults[0];
    return {
      formattedAddress: geocoderResult?.formatted_address,
      latitude: geocoderResult?.geometry.location.lat(),
      longitude: geocoderResult?.geometry.location.lng(),
      countryCodeISO: this.initLocationCountryISO(
        geocoderResult?.address_components,
      ),
    } as any;
  }

  // Applies the WHP-12496 Name rule: only accept a display name when the place
  // is a genuinely-named POI/business, never for plain street addresses.
  public extractPlaceName(
    displayName: string | null | undefined,
    types: string[] | undefined,
  ): string | null {
    if (!displayName) {
      return null;
    }
    const typeSet = new Set(types ?? []);
    const isNamedPlace =
      typeSet.has('establishment') || typeSet.has('point_of_interest');
    return isNamedPlace ? displayName : null;
  }

  private initLocationCountryISO(
    geocoderAddressComponents: google.maps.GeocoderAddressComponent[],
  ): string {
    if (!geocoderAddressComponents) {
      return null;
    }
    const country = geocoderAddressComponents.find((component) =>
      component.types?.includes('country'),
    );
    return country?.short_name ?? null;
  }

  private initLocationCountryISOFromPlaceAddressComponents(
    components: google.maps.places.AddressComponent[] | undefined,
  ): string {
    if (!components) {
      return null;
    }
    const country = components.find((c) => c.types?.includes('country'));
    return country?.shortText ?? null;
  }

  // METADATA
  public initMetadataDOM(metadataDTO?: IWHFlowMetadataDTO): WHFlowMetadataDOM {
    return new WHFlowMetadataDOM(metadataDTO);
  }

  // EQUIPMENT
  public initEquipmentDOM(equipmentDTO: IWHEquipmentDTO): WHEquipmentDOM {
    return new WHEquipmentDOM(equipmentDTO);
  }

  // EQUIPMENT NODE
  public initEquipmentNodeDOM(equipmentDTO: IWHBomNodeDTO): WHNestedNodeDOM {
    return new WHNestedNodeDOM(equipmentDTO);
  }

  public initEquipmentPreviewDOM(
    equipmentPreviewDTO: IWHEquipmentPreviewDTO,
  ): WHEquipmentPreviewDOM {
    return new WHEquipmentPreviewDOM(equipmentPreviewDTO);
  }

  // CUSTOMER
  public initCustomerDOM(customerDTO: IWHCustomerDTO): WHCustomerDOM {
    return new WHCustomerDOM(customerDTO);
  }

  public initCustomerPreviewDOM(
    customerPreviewDTO: IWHCustomerPreviewDTO,
  ): WHCustomerPreviewDOM {
    return new WHCustomerPreviewDOM(customerPreviewDTO);
  }

  // MATERIAL
  public initMaterialDOM(materialDTO: IWHMaterialDTO): WHMaterialDOM {
    return new WHMaterialDOM(materialDTO);
  }

  public initMaterialPreviewDOM(
    materialPreviewDTO: IWHMaterialPageResultDTO,
  ): WHMaterialPageResultDOM {
    return new WHMaterialPageResultDOM(materialPreviewDTO);
  }

  public initMaterialInventoryDOM(
    materialInventoryDTO: IWHMaterialInventoryDTO,
  ): WHMaterialInventoryDOM {
    return new WHMaterialInventoryDOM(materialInventoryDTO);
  }

  // TOOL
  public initToolDOM(toolDTO: IWHToolDTO): WHToolDOM {
    return new WHToolDOM(toolDTO);
  }

  public initToolPreviewDOM(
    toolPreviewDTO: IWHToolsPreviewDTO,
  ): WHToolPreviewDOM {
    return new WHToolPreviewDOM(toolPreviewDTO);
  }

  // WORKER
  public initWorkerDOM(workerDTO: IWHWorkerDTO): WHWorkerDOM {
    return new WHWorkerDOM(workerDTO);
  }

  // CHECKLIST DEFINITION
  public initChecklistDefinitionDOM(
    checklistDefinitionDTO: IWHChecklistDefinitionDTO,
  ): WHChecklistDefinitionDOM {
    return new WHChecklistDefinitionDOM(checklistDefinitionDTO);
  }

  // CHECKLIST INSTANCE
  public initChecklistInstanceDOM(
    checklistInstanceDTO: IWHChecklistInstanceDTO,
  ): WHChecklistInstanceDOM {
    return new WHChecklistInstanceDOM(checklistInstanceDTO);
  }

  // CHECKLIST INSTANCE - CHECK INSTANCE FILE
  public intiCheckInstanceFileDOM(
    checkInstanceFileDTO: IWHCheckInstanceFileDTO,
  ): WHCheckInstanceFileDOM {
    return new WHCheckInstanceFileDOM(checkInstanceFileDTO);
  }

  public initChecklistInstancePreviewDOM(
    checklistInstancePreviewDTO: IWHChecklistInstancePreviewDTO,
  ): WHChecklistInstancePreviewDOM {
    return new WHChecklistInstancePreviewDOM(checklistInstancePreviewDTO);
  }

  // WORK OBJECTS
  public initWorkObjectPreviewDOM(
    workObjectPreviewDTO: IWHWorkObjectPreviewDTO,
  ): WHWorkObjectPreviewDOM {
    return new WHWorkObjectPreviewDOM(workObjectPreviewDTO);
  }

  public initWorkObjectTypeDOM(
    workObjectDTO: IWHWorkObjectDTO,
  ): WHWorkObjectDOM {
    return this.initWorkObjectType(workObjectDTO);
  }

  private initWorkObjectType(workObjectDTO: any): WHWorkObjectDOM {
    if (!workObjectDTO) {
      return null;
    }
    switch (workObjectDTO.workObjectTypeId) {
      case WHWorkObjectTypeENUM.PROJECT:
        return this.initProjectWorkObjectDOM(workObjectDTO);
      case WHWorkObjectTypeENUM.CASE:
        return this.initCaseWorkObjectDOM(workObjectDTO);
      case WHWorkObjectTypeENUM.STANDING_ORDER:
        return this.initStandingOrderWorkObjectDOM(workObjectDTO);
      default:
        return null;
    }
  }

  public getWorkObjectTypeDOM(
    workObjectTypeId: number,
    workObjectCrudStore: IWHWorkObjectCrudStore,
  ): WHWorkObjectDOM {
    switch (workObjectTypeId) {
      case WHWorkObjectTypeENUM.PROJECT:
        return workObjectCrudStore.projectDOM;
      case WHWorkObjectTypeENUM.CASE:
        return workObjectCrudStore.caseDOM;
      case WHWorkObjectTypeENUM.STANDING_ORDER:
        return workObjectCrudStore.standingOrderDOM;
      case WHWorkObjectTypeENUM.JOB:
        return workObjectCrudStore.jobDOM;
      default:
        return null;
    }
  }

  public initWorkObjectPreviewType(
    workObjectTypeId: number,
    workObjectPreviewDTO: any,
  ): any {
    switch (workObjectTypeId) {
      case WHWorkObjectTypeENUM.PROJECT:
        return this.initProjectWorkObjectPreviewDOM(workObjectPreviewDTO);
      case WHWorkObjectTypeENUM.CASE:
        return this.initCaseWorkObjectPreviewDOM(workObjectPreviewDTO);
      case WHWorkObjectTypeENUM.STANDING_ORDER:
        return this.initStandingOrderWorkObjectPreviewDOM(workObjectPreviewDTO);
    }
  }

  // WORK OBJECT - TYPE 1 - PROJECT
  public initProjectWorkObjectPreviewDOM(
    projectWorkObjectPreviewDTO: IWHProjectWorkObjectPreviewDTO,
  ): WHProjectWorkObjectPreviewDOM {
    return new WHProjectWorkObjectPreviewDOM(projectWorkObjectPreviewDTO);
  }

  public initProjectWorkObjectDOM(
    projectWorkObjectDTO: IWHProjectWorkObjectDTO,
  ): WHProjectWorkObjectDOM {
    return new WHProjectWorkObjectDOM(projectWorkObjectDTO);
  }

  // WORK OBJECT - TYPE 2 - CASE
  public initCaseWorkObjectPreviewDOM(
    caseWorkObjectPreviewDTO: IWHCaseWorkObjectPreviewDTO,
  ): WHCaseWorkObjectPreviewDOM {
    return new WHCaseWorkObjectPreviewDOM(caseWorkObjectPreviewDTO);
  }

  public initCaseWorkObjectDOM(
    caseWorkObjectDTO: IWHCaseWorkObjectDTO,
  ): WHCaseWorkObjectDOM {
    return new WHCaseWorkObjectDOM(caseWorkObjectDTO);
  }

  // WORK OBJECT - TYPE 3 - STANDING ORDER
  public initStandingOrderWorkObjectPreviewDOM(
    standingOrderWorkObjectPreviewDTO: IWHStandingOrderWorkObjectPreviewDTO,
  ): WHStandingOrderWorkObjectPreviewDOM {
    return new WHStandingOrderWorkObjectPreviewDOM(
      standingOrderWorkObjectPreviewDTO,
    );
  }

  public initStandingOrderWorkObjectDOM(
    standingOrderWorkObjectDTO: IWHStandingOrderWorkObjectDTO,
  ): WHStandingOrderWorkObjectDOM {
    return new WHStandingOrderWorkObjectDOM(standingOrderWorkObjectDTO);
  }

  // WORK OBJECT TEMPLATE
  public initWorkObjectTemplateDOM(
    workObjectTemplateDTO: IWHWorkObjectTemplateDTO,
  ): WHWorkObjectTemplateDOM {
    return new WHWorkObjectTemplateDOM(workObjectTemplateDTO);
  }

  public initWorkObjectTemplatePreviewDOM(
    workObjectTemplatePreviewDTO: IWHWorkObjectTemplatePreviewDTO,
  ): WHWorkObjectTemplatePreviewDOM {
    return new WHWorkObjectTemplatePreviewDOM(workObjectTemplatePreviewDTO);
  }

  // WORK ORDER
  public initWorkOrderDOM(workOrderDTO: IWHWorkOrderDTO): WHWorkOrderDOM {
    return new WHWorkOrderDOM(workOrderDTO);
  }

  // WORK ORDER FILE
  public initWorkOrderFileDOM(
    workOrderFileDTO: IWHWorkOrderFileDTO,
  ): WHFileDOM {
    return new WHFileDOM().initWorkOrderFileDOM(workOrderFileDTO);
  }

  // WORK STEP
  public initWorkStepDOM(workStepDTO: IWHWorkStepDTO): WHWorkStepDOM {
    return new WHWorkStepDOM(workStepDTO);
  }

  // ENTRIES
  public initEntryPreviewDOM(
    entryPreviewDTO: IWHEntryPreviewDTO,
  ): WHEntryPreviewDOM {
    return new WHEntryPreviewDOM(entryPreviewDTO);
  }

  public initEntryDOM(entryDTO: IWHEntryDTO): WHEntryDOM {
    return new WHEntryDOM(entryDTO);
  }

  public initEntryDomByEntryTypeId(entryDTO: IWHEntryDTO) {
    switch (entryDTO.entryTypeId) {
      case 1:
        return this.initTravelEntryDOM(entryDTO as IWHTravelEntryDTO);
      case 2:
        return this.initWorkingTimeEntryDOM(entryDTO as IWHWorkingTimEntryDTO);
      case 3:
        return this.initExpenceEntryDOM(entryDTO as IWHExpenseEntryDTO);
      case 4:
        return this.initDefectEntryDOM(entryDTO as IWHDefectEntryDTO);
      case 5:
        return this.initMaterialConsumptionEntryDOM(
          entryDTO as IWHMaterialConsumptionEntryDTO,
        );
      case 6:
        return this.initMeasurmentEntryDOM(entryDTO as IWHMeasurementEntryDTO);
      default:
        return undefined;
    }
  }

  // ENTRY - TYPE 1
  public initTravelEntryDOM(
    travelEntryDTO: IWHTravelEntryDTO,
  ): WHTravelEntryDOM {
    if (!travelEntryDTO) {
      return null;
    }
    return new WHTravelEntryDOM(travelEntryDTO);
  }

  // ENTRY - TYPE 2
  public initWorkingTimeEntryDOM(
    workingTimeEntryDTO: IWHWorkingTimEntryDTO,
  ): WHWorkingTimEntryDOM {
    return new WHWorkingTimEntryDOM(workingTimeEntryDTO);
  }

  // ENTRY - TYPE 3
  public initExpenceEntryDOM(
    expenceEntryDTO: IWHExpenseEntryDTO,
  ): WHExpenseEntryDOM {
    return new WHExpenseEntryDOM(expenceEntryDTO);
  }

  // ENTRY - TYPE 4
  public initDefectEntryDOM(
    defectEntryDTO: IWHDefectEntryDTO,
  ): WHDefectEntryDOM {
    return new WHDefectEntryDOM(defectEntryDTO);
  }

  // ENTRY - TYPE 5
  public initMaterialConsumptionEntryDOM(
    materialConsumptionEntryDTO: IWHMaterialConsumptionEntryDTO,
  ): WHMaterialConsumptionEntryDOM {
    return new WHMaterialConsumptionEntryDOM(materialConsumptionEntryDTO);
  }

  // ENTRY - TYPE 6
  public initMeasurmentEntryDOM(
    measurmentEntryDTO: IWHMeasurementEntryDTO,
  ): WHMeasurementEntryDOM {
    return new WHMeasurementEntryDOM(measurmentEntryDTO);
  }

  // ENTRY FILE
  public initEntryFileDOM(entryFileDTO: IWHEntryFileDTO): WHEntryFileDOM {
    return new WHEntryFileDOM(entryFileDTO);
  }

  public initEntryFilePreviewDOM(
    entryFilePreviewDTO: IWHEntryFilePreviewDTO,
  ): WHEntryFilePreviewDOM {
    return new WHEntryFilePreviewDOM(entryFilePreviewDTO);
  }

  // REPORT FILE
  public initReportFilePreviewDOM(
    reportFilePreviewDTO: IWHReportFilePreviewDTO,
  ): WHReportFilePreviewDOM {
    return new WHReportFilePreviewDOM(reportFilePreviewDTO);
  }
}
